import { createRouter, createWebHistory } from "vue-router";

const Index = () => import("@/views/Index.vue");
const Booking = () => import("@/views/Booking.vue");
const SuccessPage = () => import("@/views/Success.vue");
const SuccessPageV2 = () => import("@/views/Quiz/V2/Success.vue");
const Quiz = () => import("@/views/Quiz/Index.vue");
const QuizV2 = () => import("@/views/Quiz/V2/Index.vue");
const QuizStopPage = () => import("@/views/Quiz/StopPage.vue");
const QuizStopPageV2 = () => import("@/views/Quiz/V2/StopPage.vue");
const QuizStopPageCustomV2 = () => import("@/views/Quiz/V2/StopPageCustom.vue");
const BackupFormV2 = () => import("@/views/Quiz/V2/BackupForm.vue");
const BackupFormV2_Success = () => import("@/views/Quiz/V2/BackupFormSuccess.vue");

const QuizSuccessPage = () => import("@/views/Quiz/Success.vue");
const ChangeBooking = () => import("@/views/ChangeBooking/Index.vue");
const ClientBookings = () => import("@/views/ChangeBooking/ClientBookings.vue");
const ChangeDay = () => import("@/views/ChangeBooking/ChangeDay.vue");
const ChangeBookingError = () => import("@/views/ChangeBooking/ErrorPage.vue");
const Invalid = () => import("@/views/Invalid.vue");
const page404 = () => import("@/views/404.vue");
const Landing = () => import("@/views/Landing.vue");
const BackupForm = () => import("@/views/BackupForm.vue");
import { ymHit } from "@/helpers/goal.js";
import { gtmSendEvent } from "@/helpers/gtm.js";
import { utmParams } from "../stores/utmStore";
import quizList from "@/common/quiz";

function redirectIfV2(to) {
  const quizId = to.params.id;
  const quiz = quizList[quizId];

  if (quiz && quiz.newDesign) {
    return { path: `/v2${to.path}`, query: { ...to.query, step: 1 } };
  }
}

const router = createRouter({
  history: createWebHistory("/welcome"),
  routes: [
    {
      path: "/",
      name: "Index",
      component: Index,
    },
    {
      path: "/booking",
      name: "Booking",
      component: Booking,
    },
    // Quiz V2 - new design & flow
    {
      path: "/v2/quiz/:id",
      name: "QuizV2",
      component: QuizV2,
      meta: { quiz: true },
    },
    // {
    //   path: "/v2/quiz/:id/success_page",
    //   name: "quiz_success_pageV2",
    //   component: SuccessPageV2,
    //   meta: { quiz: true },
    // },
    {
      path: "/v2/quiz/:id/stop_page",
      name: "quiz_stop_pageV2",
      component: QuizStopPageV2,
      meta: { quiz: true },
    },
    {
      path: "/v2/quiz/:id/custom_stop",
      name: "quiz_stop_page_customV2",
      component: QuizStopPageCustomV2,
      meta: { quiz: true },
    },
    {
      path: "/v2/quiz/:id/backup-form-quiz",
      name: "quiz_backupForm_customV2",
      component: BackupFormV2,
      meta: { quiz: true },
    },
    {
      path: "/v2/quiz/:id/backup-form-quiz/success",
      name: "quiz_backupForm_success_customV2",
      component: BackupFormV2_Success,
      meta: { quiz: true },
    },
    // ----------------------------------------
    {
      path: "/quiz/:id",
      name: "Quiz",
      component: Quiz,
      meta: { quiz: true },
      beforeEnter: [redirectIfV2],
    },
    {
      path: "/quiz",
      name: "Quiz_first",
      component: Quiz,
      meta: { quiz: true },
    },
    {
      path: "/quiz/:id/success_page",
      name: "quiz_success_page",
      component: QuizSuccessPage,
      meta: { quiz: true },
    },
    {
      path: "/quiz/:id/stop_page",
      name: "quiz_stop_page",
      component: QuizStopPage,
      meta: { quiz: true },
      beforeEnter: [redirectIfV2],
    },
    {
      path: "/landing",
      name: "Landing",
      component: Landing,
      meta: { landing: true },
    },
    {
      path: "/detail/:id",
      name: "SuccessPage",
      component: SuccessPage,
      meta: { success: true },
    },
    {
      path: "/v2/detail/:id",
      name: "SuccessPageV2",
      component: SuccessPageV2,
      meta: { success: true, quiz: true },
    },
    {
      path: "/invalid_page",
      name: "quiz_invalid_page",
      component: Invalid,
      meta: { quiz: true },
    },
    {
      path: "/change_booking",
      name: "ChangeBooking",
      component: ChangeBooking,
    },
    {
      path: "/change_booking/client_bookings",
      name: "ClientBookings",
      component: ClientBookings,
    },
    {
      path: "/change_booking/change_day",
      name: "ChangeDay",
      component: ChangeDay,
    },
    {
      path: "/change_booking/error",
      name: "ChangeBookingError",
      component: ChangeBookingError,
    },
    {
      path: "/backup-form",
      name: "BackupForm",
      component: BackupForm,
    },
    {
      path: "/404",
      name: "404",
      component: page404,
    },
  ],
});

function setRobots(content) {
  if (typeof document === 'undefined') return;
  let tag = document.querySelector('meta[name="robots"]');
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', 'robots');
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

router.afterEach((to, from, failure) => {
  const shouldNoindex = to.matched.some(r => r.meta?.quiz === true);
  setRobots(shouldNoindex ? 'noindex, nofollow' : 'index, follow');

  if (!failure) ymHit(to.fullPath);

  // Send events to GA4
  const baseName = to.name;
  const pageViewEventMap = new Map();
  pageViewEventMap.set("Quiz_first", "ab_page_view_intro_page_quiz");
  pageViewEventMap.set("Landing", "ab_page_view_intro_page_quiz");
  pageViewEventMap.set(
    "Index",
    "ab_page_view_autobooking_landing_w_parent_info"
  );
  pageViewEventMap.set("Booking", "ab_page_view_TC_pick_datetime_page");
  pageViewEventMap.set("ClientBookings", "ab_page_view_manage_bookings");
  pageViewEventMap.set("ChangeBooking", "ab_page_view_manage_bookings");
  pageViewEventMap.set("BackupForm", "ab_page_view_opening_backup_form");

  if (pageViewEventMap.has(baseName)) {
    const eventName = pageViewEventMap.get(baseName);
    gtmSendEvent(eventName);
  }
  // ------------------------------------------------------------
});

// Presave 'segment' and other query params
router.beforeEach((to, from) => {
  // Pre-saving query params when routing
  const preSavedParamNames = [
    "segment",
    "teenagers",
    "preferred_countries1",
    "preferred_countries2",
    "preferred_countries3",
    "preferred_countries4",
    "preferred_countries5",
    "preferred_countries6",
    "preferred_countries7",
    "preferred_countries8",
    "preferred_countries9",
    "preferred_countries10",
    "only_countries1",
    "only_countries2",
    "only_countries3",
    "only_countries4",
    "only_countries5",
    "only_countries6",
    "only_countries7",
    "only_countries8",
    "only_countries9",
    "only_countries10",
    "name",
    "phone",
    "email",
    "country",
    "landingID",
    "success_page",
    "distribution_tag_ids",
    "age",
    "childName",
    "fb_pixel_mena",
    "fb_pixel_mena2",
    "fb_pixel_it",
    "fb_pixel_pl",
    "fb_pixel_latam",
    "fb_pixel_br",
    "promocode",
    "computer",
    "consent",
    "language-check",
    "bg",
    "acquisition_source",
    "ref_code",
    ...utmParams,
  ];
  let preSavedParamList = null;

  preSavedParamNames.forEach((param) => {
    if (from.query[param] && !to.query[param]) {
      if (!preSavedParamList) preSavedParamList = {};
      preSavedParamList[param] = from.query[param];
    }
  });

  if (preSavedParamList)
    return { path: to.path, query: { ...to.query, ...preSavedParamList } };
  // ------------------------------------------------------------

  return true;
});

export default router;
